import * as React from "react";
import _ from "lodash";
import { AutoSizer, List, ListRowProps } from "react-virtualized";
import styled from "styled-components";

import * as S from "@styled";
import { ERuleStatus, IAppReducerState, IRuleData } from "monitoringRules/shared/model";
import { AppContext } from "monitoringRules/App";

const StyledRuleStatusIndicatorContainer = styled(S.FlexContainer)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 22px 0 20px;
`;

const StyledRuleStatusTextItem = styled(S.FlexItem)`
    width: 100%;
    overflow: hidden;

    h3, span {
        margin-left: 0;
    }

    span {
        font-size: 1rem;
    }
`;

export const RuleList: React.FC = () => {
    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);

    const getSearchedRules = () => {
        return state?.rules?.filter((x) => x.name.toLowerCase().includes(state.searchString?.toLowerCase()));
    };

    const memoizedRules = React.useMemo(() => getSearchedRules(), [state.searchString, state.rules]) ?? [];


    React.useEffect(() => {
        if (state.selectedRuleId != null && !memoizedRules.some((rule) => rule.id === state.selectedRuleId)) {
            dispatch({ type: "SET_SELECTED_RULE_ID", id: null });
        }
    }, [memoizedRules]);

    if (!_.isEmpty(state.searchString) && _.isEmpty(memoizedRules)) {

        return <div>
            <h4>No rules were found with the current search filter.</h4>
        </div>
    }
    return <S.ListContainer className="virtualized-container">
        <AutoSizer>
            {({ height, width }) => (
                <List
                    height={height}
                    width={width}
                    rowHeight={120}
                    overscanRowCount={10}
                    scrollToIndex={0}
                    rowCount={memoizedRules.length}
                    rowRenderer={(listRowProps) =>
                        <RuleListItem
                            rule={memoizedRules[listRowProps.index]}
                            listLength={memoizedRules.length}
                            {...listRowProps}
                        />
                    }
                />
            )}
        </AutoSizer>
    </S.ListContainer>
}

export const RuleListItem: React.FC<ListRowProps & { listLength: number, rule: IRuleData }> = (props) => {
    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);

    const handleSelectRule = () => {
        dispatch({ type: "SET_SELECTED_RULE_ID", id: props.rule.id });
        if (state.showNewRuleEditor) {
            dispatch({ type: "SET_SHOW_NEW_RULE_EDITOR", showNewRuleEditor: false });
        }
    }

    return <S.ListItem
        style={props.style}
        $isActive={props.rule.id === state.selectedRuleId}
        index={props.index}
        key={props.rule.id}
        onClick={handleSelectRule}
        $isLast={props.index === props.listLength - 1}
        className="rule-item"
    >
        <S.FlexContainer asRow={true} noWrap={true} isFullWidth={true}>
            <StyledRuleStatusIndicatorContainer>
                <RuleStatusIndicator lastStatus={props.rule.lastStatus} alertsCount={props.rule.alertsCount} />
            </StyledRuleStatusIndicatorContainer>
            <StyledRuleStatusTextItem>
                <S.ListItemTitle id={`rule-${props.rule.name}`}>
                    {props.rule.name}
                </S.ListItemTitle>

                <S.ListItemSpan style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ textOverflow: "ellipsis", overflow: "hidden" }} title={props.rule.description}>{props.rule.description}</span>
                    <span>Recipients:&nbsp;
                        {!_.isEmpty(props.rule.emailRecipients) ? props.rule.emailRecipients.join(", ")
                            : "None"}
                    </span>
                    <span>Processes:&nbsp;
                        {!_.isEmpty(props.rule.config.processTriggerValues) ? props.rule.config.processTriggerValues?.map((x) => `${x.processName} - ${x.agentGroupDisplayName}`).join(", ")
                            : "None"}
                    </span>
                </S.ListItemSpan>
            </StyledRuleStatusTextItem>
        </S.FlexContainer>
    </S.ListItem>
}


const RuleStatusIndicator: React.FC<{ lastStatus: ERuleStatus | undefined, alertsCount: number }> = (props) => {

    if (props.alertsCount > 0) {
        return <S.ErrorChip style={{ maxWidth: 75, textOverflow: "ellipsis", overflow: "hidden" }} title={"Alerts: " + props.alertsCount?.toString()}>{props.alertsCount}</S.ErrorChip>;
    }
    if (props.lastStatus === ERuleStatus.Ok) {
        return <i style={{ color: S.Colors.greenPrimary, fontSize: 36 }} className="ci-check_big" />
    }

    return <i style={{ color: S.Colors.blueDark, fontSize: 36 }} title="Rule not yet processed" className="ci-help_questionmark" />;
}